/**
 * Purpose:
 *   Localizes menu item labels from common namespace label keys.
 *
 * Notes:
 *   Keeps menu configuration static while rendering user-facing labels via i18n.
 */

import type { TFunction } from 'i18next';
import type { MenuItemConfig } from '@/types/menu';

export function localizeMenuItems(
  items: MenuItemConfig[],
  t: TFunction<'common'>
): MenuItemConfig[] {
  return items.map((item) => ({
    ...item,
    label: item.labelKey ? t(item.labelKey) : item.label,
    children: item.children ? localizeMenuItems(item.children, t) : undefined,
  }));
}

export function filterMenuItemsByPermissions(
  items: MenuItemConfig[],
  permissions: readonly string[]
): MenuItemConfig[] {
  return items.flatMap((item) => {
    if (item.requiredPermission && !permissions.includes(item.requiredPermission)) {
      return [];
    }

    const children = item.children
      ? filterMenuItemsByPermissions(item.children, permissions)
      : undefined;

    if (item.children && children?.length === 0) {
      return [];
    }

    return [{ ...item, children }];
  });
}
